import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, ViewEncapsulation, inject, input } from '@angular/core';
import {LiveAnnouncer} from '@angular/cdk/a11y';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import {Store} from '@ngrx/store';
import {Observable, Subject, combineLatest} from 'rxjs';
import {filter, map, takeUntil} from 'rxjs/operators';
import {getLoggedInUser} from '@app/stores/authentication/authentication.selectors';
import {MenuMode, NavigationAction} from '@app/modules/shared/models/navigation';
import {ThemeMode, ThemeService} from '@app/services/theme.service';
import {NavigationMenuService} from '@app/services/navigation-menu.service';
import {NavigationStateService} from '@app/services/navigation-state.service';
import {NavLayoutService} from '@app/services/nav-layout.service';
import {ResolvedNavNode} from '../model/nav-tree';
import { AsyncPipe } from '@angular/common';

/** Theme and sign-out, for a session whose response carried no menu - never a dead end. */
const FALLBACK_USER_MENU: ResolvedNavNode[] = [
  {id: 'user.theme', type: 'widget', action: 'themeSwitcher', label: 'Theme'},
  {id: 'user.dividerSession', type: 'divider'},
  {id: 'user.logout', type: 'action', action: 'logout', label: 'Log out', icon: 'fa-solid fa-right-from-bracket'},
].map((node: any) => ({
  label: '', icon: null, path: null, queryParams: null, href: null, external: false, exact: false, context: null,
  action: null, keywords: [], menu: 'user', depth: 1, parentId: null, ancestors: [], children: [], ...node,
}));

/**
 * The account menu, as the server sent it: profile and security links, the theme and menu
 * layout switches, Stop impersonation while impersonating, and Log out. In the top bar this
 * element is the navbar's own `.login-part` dropdown, so its look is unchanged.
 */
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector -- sits on the menu's own <li> to keep the list markup
  selector: '[app-nav-user-menu]',
  templateUrl: './nav-user-menu.component.html',
  styleUrls: ['./nav-user-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [RouterLink, AsyncPipe],
  host: {
    '(document:click)': 'onDocumentClick($event)',
    '(keydown.escape)': 'onEscape()',
    '[class.show]': 'shown',
  }
})
export class NavUserMenuComponent implements OnDestroy {
  private store = inject<Store<any>>(Store);
  private themeService = inject(ThemeService);
  private menu = inject(NavigationMenuService);
  private navigationState = inject(NavigationStateService);
  private navLayout = inject(NavLayoutService);
  private announcer = inject(LiveAnnouncer);
  private host = inject<ElementRef<HTMLElement>>(ElementRef);
  private cdr = inject(ChangeDetectorRef);

  /** 'top': the navbar's dropdown. 'bar': the compact avatar in the side menu's top bar. */
  readonly variant = input<'top' | 'bar'>('top');

  open = false;

  readonly items$: Observable<ResolvedNavNode[]> = combineLatest([this.menu.available$, this.menu.user$]).pipe(
    map(([available, items]) => available && items.length ? items : FALLBACK_USER_MENU)
  );
  readonly user = this.store.selectSignal(getLoggedInUser);
  readonly themeMode$: Observable<ThemeMode> = this.themeService.themeMode$;
  readonly menuMode$: Observable<MenuMode> = this.navigationState.menuMode$;
  readonly isMobile$ = this.navLayout.viewport$.pipe(map((viewport) => viewport === 'mobile'));
  readonly menuAvailable$ = this.menu.available$;

  private readonly destroyed = new Subject<void>();

  get shown(): boolean {
    return this.open;
  }

  constructor() {
    const router = inject(Router);

    router.events.pipe(filter((event) => event instanceof NavigationEnd), takeUntil(this.destroyed))
      .subscribe(() => {
        this.open = false;
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  toggle(): void {
    this.open = !this.open;
  }

  displayName(user: any): string {
    if (!user) {
      return '';
    }
    const name = [user.first_name, user.last_name].filter(Boolean).join(' ');
    return name || user.email;
  }

  avatar(user: any): string {
    return user?.profile_img || '../assets/images/project-default.png';
  }

  run(action: NavigationAction): void {
    this.open = false;
    this.menu.runAction(action);
  }

  setTheme(mode: ThemeMode, event: Event): void {
    event.stopPropagation();
    this.themeService.setThemeMode(mode);
  }

  setMenuMode(mode: MenuMode, event: Event): void {
    event.stopPropagation();
    this.navigationState.setMenuMode(mode);
    this.announcer.announce(mode === 'side' ? 'Side menu on' : 'Top menu on');
  }

  trackById(_index: number, node: ResolvedNavNode): string {
    return node.id;
  }

  onDocumentClick(event: MouseEvent): void {
    if (this.open && !this.host.nativeElement.contains(event.target as Node)) {
      this.open = false;
      this.cdr.markForCheck();
    }
  }

  onEscape(): void {
    if (!this.open) {
      return;
    }
    this.open = false;
    this.host.nativeElement.querySelector<HTMLElement>('[data-sn-user-toggle]')?.focus();
  }
}
